import { Box, Button, Typography, LinearProgress } from "@mui/material";
import { useEffect, useState } from "react";

export default function AssessmentPlayer({
  assessment,
  onCorrect,
}) {
  const [questionIndex, setQuestionIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showResult, setShowResult] = useState(false);

  const validQuestions =
    assessment?.questions?.filter((q) => {
      const hasQuestion =
        q.question?.html?.trim() ||
        q.question?.imageUrl ||
        q.question?.pdfUrl ||
        q.question?.attachmentUrl;

      const hasOption = q.options?.some(
        (opt) =>
          opt.html?.trim() ||
          opt.imageUrl ||
          opt.attachmentUrl
      );

      return hasQuestion && hasOption;
    }) || [];


  useEffect(() => {
    setQuestionIndex(0);
    setSelectedIndex(null);
    setShowResult(false);
  }, [assessment]);


  useEffect(() => {
    setSelectedIndex(null);
    setShowResult(false);
  }, [questionIndex]);


  if (!validQuestions.length) {
    return (
      <Box p={3}>
        <Typography>
          No assessment questions found.
        </Typography>
      </Box>
    );
  }

  const currentQuestion = validQuestions[questionIndex];

  if (!currentQuestion) {
    return (
      <Box p={3}>
        <Typography>
          Assessment question not found.
        </Typography>
      </Box>
    );
  }


  const correctIndex =
    currentQuestion.options?.findIndex(
      (option) => option.isCorrect
    ) ?? -1;


  const goToNextQuestion = () => {
    if (questionIndex < validQuestions.length - 1) {
      setQuestionIndex((prev) => prev + 1);
    } else {
      // Assessment finished
      onCorrect?.();
    }
  };


  const handleOptionClick = (index) => {
    if (showResult) return;

    setSelectedIndex(index);
    setShowResult(true);

    // Correct answer
    if (index === correctIndex) {
      setTimeout(() => {
        goToNextQuestion();
      }, 1200);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Progress */}
      <Typography
        variant="body2"
        color="text.secondary"
        mb={1}
      >
        Question {questionIndex + 1} of {validQuestions.length}
      </Typography>

      <LinearProgress
        variant="determinate"
        value={
          ((questionIndex + 1) / validQuestions.length) * 100
        }
        sx={{ mb: 3 }}
      />

      {/* Question */}
      <Box
        mb={3}
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",

          // Make HTML content responsive
          "& *": {
            maxWidth: "100%",
            boxSizing: "border-box",
          },

          // Prevent long text/words from overflowing
          "& p, & div, & span, & td, & th": {
            maxWidth: "100%",
            overflowWrap: "break-word",
            wordBreak: "break-word",
          },

          // Images inside question HTML
          "& img": {
            maxWidth: "100% !important",
            height: "auto !important",
            objectFit: "contain",
          },

          // Tables inside question HTML
          "& table": {
            width: "100% !important",
            maxWidth: "100%",
            tableLayout: "fixed",
            overflowWrap: "break-word",
          },

          "& pre": {
            maxWidth: "100%",
            overflowX: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          },
        }}
      >
        {currentQuestion.question?.html && (
          <Typography
            variant="h6"
            sx={{
              width: "100%",
              maxWidth: "100%",
              overflowWrap: "break-word",
              wordBreak: "break-word",
              lineHeight: 1.5,
            }}
            dangerouslySetInnerHTML={{
              __html: currentQuestion.question.html,
            }}
          />
        )}

        {currentQuestion.question?.imageUrl && (
          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
              mt: 1.5,
            }}
          >
            <img
              src={currentQuestion.question.imageUrl}
              alt="Question"
              style={{
                display: "block",
                maxWidth: "100%",
                width: "auto",
                height: "auto",
                maxHeight: "400px",
                objectFit: "contain",
              }}
            />
          </Box>
        )}

        {currentQuestion.question?.pdfUrl && (
          <Button
            href={currentQuestion.question.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            View PDF
          </Button>
        )}

        {currentQuestion.question?.attachmentUrl && (
          <Button
            href={currentQuestion.question.attachmentUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ ml: 1 }}
          >
            View Attachment
          </Button>
        )}
      </Box>

      {/* Options */}
      {currentQuestion.options?.map((opt, index) => {
        const isCorrect = index === correctIndex;
        const isSelected = index === selectedIndex;

        let bgColor = "transparent";

        if (showResult) {
          if (isCorrect) {
            bgColor = "#C8E6C9";
          } else if (isSelected) {
            bgColor = "#FFCDD2";
          }
        }

        return (
          <Button
            key={opt.id || index}
            fullWidth
            variant="outlined"
            onClick={() => handleOptionClick(index)}
            disabled={showResult}
            sx={{
              justifyContent: "flex-start",
              alignItems: "center",
              textAlign: "left",

              mb: 2,

              // Same padding for text and image options
              px: 2,
              py: 1.5,

              minHeight: "auto",

              backgroundColor: bgColor,

              overflow: "hidden",

              "&:hover": {
                backgroundColor: bgColor,
              },
            }}
          >
            <Box
              width="100%"
              sx={{
                minWidth: 0,
              }}
            >
              {/* TEXT / HTML OPTION */}
              {opt?.html && (
                <Box
                  sx={{
                    width: "100%",
                    overflowWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: opt.html,
                  }}
                />
              )}

              {/* IMAGE OPTION */}
              {opt?.imageUrl && (
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={opt.imageUrl}
                    alt={`Option ${index + 1}`}
                    style={{
                      display: "block",
                      maxWidth: "100%",
                      width: "auto",
                      height: "auto",
                      maxHeight: "100px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              )}

              {/* ATTACHMENT */}
              {opt?.attachmentUrl && (
                <Box mt={1}>
                  <Button
                    href={opt.attachmentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    View Attachment
                  </Button>
                </Box>
              )}
            </Box>
          </Button>
        );
      })}

      {/* Wrong Answer */}
      {showResult && selectedIndex !== correctIndex && (
        <Box mt={3}>
          <Typography color="error" mb={2}>
            Wrong answer.
          </Typography>

          <Typography fontWeight={600}>
            Correct Answer:
          </Typography>

          {correctIndex >= 0 && (
            <>
              {currentQuestion.options[correctIndex]?.html && (
                <Typography
                  dangerouslySetInnerHTML={{
                    __html:
                      currentQuestion.options[correctIndex].html,
                  }}
                />
              )}

              {currentQuestion.options[correctIndex]?.imageUrl && (
                <img
                  src={
                    currentQuestion.options[correctIndex].imageUrl
                  }
                  alt="Correct answer"
                  style={{
                    maxWidth: "100%",
                    marginTop: 8,
                  }}
                />
              )}
            </>
          )}

          <Button
            variant="contained"
            sx={{ mt: 3 }}
            onClick={goToNextQuestion}
          >
            {questionIndex === validQuestions.length - 1
              ? "Finish Assessment"
              : "Next Question"}
          </Button>
        </Box>
      )}
    </Box>
  );
}